import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Hls from "hls.js";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  fetchCameraSnapshotBlob,
  startCameraRelay,
} from "../../api/cameras.ts";
import { resolveOmniMediaUrl } from "../../api/client.ts";
import {
  PRESET_GRID_COLUMNS,
  PRESET_GRID_MARKER_FONT_PX,
  PRESET_GRID_ROWS,
  PRESET_GRID_TOTAL,
  PRESET_GRID_FLAT_TOP_OFFSET,
  presetGridIdFromFlatNorm,
} from "../../lib/presetGrid.ts";
import { EQUIRECT_VIDEO_ASPECT } from "../../lib/ptzPanoramaMap.ts";
import { equirectNormToPatrolHotspotDeg } from "../ConstructionSiteManagement/patrol/patrolHotspotProjection.js";
import {
  HotspotPano360ImageView,
  type HotspotPano360Marker,
} from "./HotspotPano360ImageView.tsx";
import { PresetMappingPtzLiveView } from "./PresetMappingPtzLiveView.tsx";

export type SavedHotspotMarker = {
  id: string;
  yaw_deg: number;
  pitch_deg: number;
  label?: string;
};

type PresetLensMode = "360" | "ptz";

type CameraHotspotAnglePickerProps = {
  cameraId: string;
  has360Stream: boolean;
  /** True when the camera has a PTZ stream URL configured. */
  hasPtzStream?: boolean;
  yawDeg: number;
  pitchDeg: number;
  pinDirectionDeg: number;
  savedHotspots?: SavedHotspotMarker[];
  activeHotspotId?: string | null;
  onSelectSavedHotspot?: (id: string) => void;
  presetGridEnabled?: boolean;
  selectedPresetGridId?: number | null;
  onPresetGridSelect?: (gridId: number) => void;
  onAnglesChange: (angles: {
    yaw_deg: number;
    pitch_deg: number;
    pin_direction_deg?: number;
  }) => void;
};

function captureVideoFrameDataUrl(video: HTMLVideoElement): string | null {
  if (video.videoWidth <= 0 || video.videoHeight <= 0) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0);
  try {
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch {
    return null;
  }
}

export function CameraHotspotAnglePicker({
  cameraId,
  has360Stream,
  hasPtzStream = false,
  yawDeg,
  pitchDeg,
  pinDirectionDeg,
  savedHotspots = [],
  activeHotspotId = null,
  onSelectSavedHotspot,
  presetGridEnabled = false,
  selectedPresetGridId = null,
  onPresetGridSelect,
  onAnglesChange,
}: CameraHotspotAnglePickerProps) {
  const captureVideoRef = useRef<HTMLVideoElement>(null);
  const flatViewHostRef = useRef<HTMLDivElement>(null);
  const captureAttemptedRef = useRef(false);
  const [panoramaSrc, setPanoramaSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [viewerYaw, setViewerYaw] = useState(yawDeg);
  const [viewerPitch, setViewerPitch] = useState(pitchDeg);
  const [centerViewNonce, setCenterViewNonce] = useState(0);
  const [presetViewMode, setPresetViewMode] = useState<"flat" | "immersive">(
    "flat",
  );
  const [presetLensMode, setPresetLensMode] = useState<PresetLensMode>("360");

  useEffect(() => {
    if (!presetGridEnabled) {
      setPresetViewMode("flat");
      setPresetLensMode("360");
    }
  }, [presetGridEnabled]);

  useEffect(() => {
    if (presetGridEnabled && presetLensMode === "ptz" && !hasPtzStream) {
      setPresetLensMode("360");
    }
  }, [hasPtzStream, presetGridEnabled, presetLensMode]);

  const showPresetPtzView =
    presetGridEnabled && presetLensMode === "ptz" && hasPtzStream;
  const showPresetFlatView =
    presetGridEnabled && presetLensMode === "360" && presetViewMode === "flat";
  const showPreset360View =
    presetGridEnabled &&
    presetLensMode === "360" &&
    presetViewMode === "immersive";
  const load360Preview =
    Boolean(cameraId && has360Stream) &&
    (!presetGridEnabled || presetLensMode === "360");

  useEffect(() => {
    if (!load360Preview) {
      setPanoramaSrc(null);
      setLoadError(
        has360Stream
          ? null
          : "Add a 360° streaming URL on this camera to mark angles on video.",
      );
      return undefined;
    }

    let cancelled = false;
    const ac = new AbortController();

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setPanoramaSrc(null);
      setHlsUrl(null);

      try {
        const blob = await fetchCameraSnapshotBlob(
          cameraId,
          String(Date.now()),
          ac.signal,
        );
        if (cancelled) return;
        setPanoramaSrc(URL.createObjectURL(blob));
        setLoading(false);
        return;
      } catch {
        /* fall through to HLS first frame */
      }

      try {
        const relay = await startCameraRelay(cameraId);
        if (cancelled) return;
        const playlist = relay.hls360;
        if (!playlist) {
          setLoadError(
            "Could not load camera preview. Check the 360° stream and relay.",
          );
          setLoading(false);
          return;
        }
        captureAttemptedRef.current = false;
        setHlsUrl(playlist);
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : "Could not start camera stream.",
        );
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [cameraId, has360Stream, load360Preview]);

  useEffect(() => {
    return () => {
      if (panoramaSrc?.startsWith("blob:")) {
        URL.revokeObjectURL(panoramaSrc);
      }
    };
  }, [panoramaSrc]);

  const handleEquirectPick = useCallback(
    (normX: number, normY: number) => {
      if (presetGridEnabled) {
        const gridId = presetGridIdFromFlatNorm(normX, normY);
        if (gridId !== null) onPresetGridSelect?.(gridId);
        return;
      }
      const { yaw_deg, pitch_deg } = equirectNormToPatrolHotspotDeg(
        normX,
        normY,
      );
      onAnglesChange({
        yaw_deg: Math.round(yaw_deg * 10) / 10,
        pitch_deg: Math.round(pitch_deg * 10) / 10,
        pin_direction_deg: pinDirectionDeg,
      });
    },
    [onAnglesChange, onPresetGridSelect, pinDirectionDeg, presetGridEnabled],
  );

  const panoMarkers = useMemo((): HotspotPano360Marker[] => {
    const list: HotspotPano360Marker[] = savedHotspots
      .filter((h) => h.id !== activeHotspotId)
      .map((h) => ({
        id: h.id,
        yaw_deg: h.yaw_deg,
        pitch_deg: h.pitch_deg,
        label: h.label,
        variant: "saved" as const,
      }));

    const hasAngles =
      Number.isFinite(yawDeg) &&
      Number.isFinite(pitchDeg) &&
      (yawDeg !== 0 || pitchDeg !== 0 || activeHotspotId != null);

    if (hasAngles) {
      list.push({
        id: activeHotspotId ?? "__active__",
        yaw_deg: yawDeg,
        pitch_deg: pitchDeg,
        label:
          activeHotspotId != null
            ? savedHotspots.find((h) => h.id === activeHotspotId)?.label
            : "New",
        variant: "active",
      });
    }

    return list;
  }, [activeHotspotId, pitchDeg, savedHotspots, yawDeg]);

  const captureFromVideo = useCallback(() => {
    if (captureAttemptedRef.current) return;
    const video = captureVideoRef.current;
    if (!video || video.videoWidth <= 0) return;
    captureAttemptedRef.current = true;
    video.pause();
    const dataUrl = captureVideoFrameDataUrl(video);
    if (dataUrl) {
      setPanoramaSrc(dataUrl);
      setHlsUrl(null);
      setLoading(false);
      return;
    }
    captureAttemptedRef.current = false;
    setLoadError("Could not capture a frame from the live stream.");
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!hlsUrl || panoramaSrc) return undefined;
    const video = captureVideoRef.current;
    if (!video) return undefined;
    const src = resolveOmniMediaUrl(hlsUrl);
    if (!src) {
      setLoadError("Invalid stream URL.");
      setLoading(false);
      return undefined;
    }

    const onFrame = () => captureFromVideo();

    let hls: Hls | null = null;
    video.addEventListener("loadeddata", onFrame, { once: true });
    video.addEventListener("playing", onFrame, { once: true });

    if (Hls.isSupported() && /\.m3u8(\?|$)/i.test(src)) {
      hls = new Hls({ enableWorker: true });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls?.loadSource(src));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => undefined);
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          setLoadError("Live stream failed. Check relay / 360° URL.");
          setLoading(false);
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      void video.play().catch(() => undefined);
    } else {
      setLoadError("HLS is not supported in this browser.");
      setLoading(false);
    }

    return () => {
      video.removeEventListener("loadeddata", onFrame);
      video.removeEventListener("playing", onFrame);
      hls?.destroy();
    };
  }, [captureFromVideo, hlsUrl, panoramaSrc]);

  const centerOnHotspot = useCallback(() => {
    setViewerYaw(yawDeg);
    setViewerPitch(pitchDeg);
    setCenterViewNonce((n) => n + 1);
  }, [pitchDeg, yawDeg]);

  const refreshFrame = useCallback(() => {
    if (!cameraId || !has360Stream) return;
    setLoading(true);
    setLoadError(null);
    if (panoramaSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(panoramaSrc);
    }
    setPanoramaSrc(null);
    captureAttemptedRef.current = false;
    void (async () => {
      try {
        const blob = await fetchCameraSnapshotBlob(
          cameraId,
          String(Date.now()),
        );
        setPanoramaSrc(URL.createObjectURL(blob));
      } catch {
        try {
          const relay = await startCameraRelay(cameraId);
          if (relay.hls360) setHlsUrl(relay.hls360);
          else setLoadError("Could not refresh camera frame.");
        } catch (e) {
          setLoadError(
            e instanceof Error ? e.message : "Could not refresh frame.",
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [cameraId, has360Stream, panoramaSrc]);

  const handleFlatViewClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!presetGridEnabled) return;
      const host = flatViewHostRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const width = Math.max(rect.width, 1);
      const height = Math.max(rect.height, 1);
      const normX = (event.clientX - rect.left) / width;
      const normY = (event.clientY - rect.top) / height;
      const gridId = presetGridIdFromFlatNorm(normX, normY);
      if (gridId !== null) onPresetGridSelect?.(gridId);
    },
    [onPresetGridSelect, presetGridEnabled],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, color: "#364153", mb: 0.75 }}
      >
        {presetGridEnabled
          ? showPresetPtzView
            ? "Preset mapping — PTZ live view"
            : showPreset360View
              ? "Preset mapping — 360° view"
              : "Preset mapping — flat panorama view"
          : "Hotspot angle — 360° view"}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
        {presetGridEnabled
          ? showPresetPtzView
            ? "Live PTZ stream: use the direction pad (bottom-right) to pan/tilt and zoom (bottom-left). Switch to 360° to pick a grid cell (1–36)."
            : showPreset360View
              ? "Drag to look around. Click any point on the 360° view to auto-select its grid (1-36)."
              : "Click on the flat panorama to auto-select grid 1-36 (grid starts below the top band)."
          : "Drag to look around. Click (or right-click) on the view to place the hotspot."}
      </Typography>
      {presetGridEnabled ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant={presetLensMode === "360" ? "contained" : "outlined"}
            onClick={() => setPresetLensMode("360")}
            sx={{ textTransform: "none" }}
          >
            360
          </Button>
          <Button
            size="small"
            variant={showPresetPtzView ? "contained" : "outlined"}
            onClick={() => setPresetLensMode("ptz")}
            disabled={!hasPtzStream}
            sx={{ textTransform: "none" }}
          >
            PTZ
          </Button>
          {presetLensMode === "360" ? (
            <>
              <Button
                size="small"
                variant={showPreset360View ? "contained" : "outlined"}
                onClick={() => setPresetViewMode("immersive")}
                sx={{ textTransform: "none" }}
              >
                360 view
              </Button>
              <Button
                size="small"
                variant={showPresetFlatView ? "contained" : "outlined"}
                onClick={() => setPresetViewMode("flat")}
                sx={{ textTransform: "none" }}
              >
                Flat view
              </Button>
            </>
          ) : null}
        </Box>
      ) : null}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: showPresetPtzView
            ? "16 / 9"
            : String(EQUIRECT_VIDEO_ASPECT),
          minHeight: 240,
          borderRadius: 1,
          border: "1px solid #E5E7EB",
          bgcolor: "#0b1220",
          overflow: "hidden",
        }}
      >
        {showPresetPtzView && cameraId ? (
          <PresetMappingPtzLiveView cameraId={cameraId} />
        ) : null}

        {!showPresetPtzView && loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              zIndex: 4,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#93C5FD" }} />
            <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
              Loading camera frame…
            </Typography>
          </Box>
        ) : null}

        {!showPresetPtzView &&
        panoramaSrc &&
        (!presetGridEnabled || showPreset360View) ? (
          <HotspotPano360ImageView
            imageUrl={panoramaSrc}
            markers={panoMarkers}
            lookAtYawDeg={viewerYaw}
            lookAtPitchDeg={viewerPitch}
            centerViewNonce={centerViewNonce}
            onEquirectPick={handleEquirectPick}
            onViewerRotation={(y, p) => {
              setViewerYaw(y);
              setViewerPitch(p);
            }}
            onMarkerClick={onSelectSavedHotspot}
            presetGridEnabled={presetGridEnabled && showPreset360View}
            selectedPresetGridId={selectedPresetGridId}
            onPresetGridSelect={onPresetGridSelect}
          />
        ) : null}

        {!showPresetPtzView && panoramaSrc && showPresetFlatView ? (
          <Box
            ref={flatViewHostRef}
            sx={{
              position: "absolute",
              inset: 0,
              userSelect: "none",
            }}
          >
            <Box
              component="img"
              src={panoramaSrc}
              alt="Flat panorama"
              draggable={false}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${PRESET_GRID_FLAT_TOP_OFFSET * 100}%`,
                borderTop: "1.5px dashed rgba(255,255,255,0.45)",
                pointerEvents: "none",
              }}
            />
            <Box
              onClick={handleFlatViewClick}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                top: `${PRESET_GRID_FLAT_TOP_OFFSET * 100}%`,
                cursor: "crosshair",
              }}
            >
              {Array.from({ length: PRESET_GRID_COLUMNS - 1 }, (_, idx) => (
                <Box
                  key={`v-${idx}`}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${((idx + 1) * 100) / PRESET_GRID_COLUMNS}%`,
                    borderLeft: "1.5px dashed rgba(255,255,255,0.65)",
                  }}
                />
              ))}
              {Array.from({ length: PRESET_GRID_ROWS - 1 }, (_, idx) => (
                <Box
                  key={`h-${idx}`}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `${((idx + 1) * 100) / PRESET_GRID_ROWS}%`,
                    borderTop: "1.5px dashed rgba(255,255,255,0.65)",
                  }}
                />
              ))}
              {Array.from(
                { length: PRESET_GRID_TOTAL },
                (_, idx) => idx + 1,
              ).map((gridId) => {
                const selected = selectedPresetGridId === gridId;
                const rowIndex = Math.floor((gridId - 1) / PRESET_GRID_COLUMNS);
                const colIndex = (gridId - 1) % PRESET_GRID_COLUMNS;
                return (
                  <Box
                    key={gridId}
                    sx={{
                      position: "absolute",
                      left: `${((colIndex + 0.5) * 100) / PRESET_GRID_COLUMNS}%`,
                      top: `${((rowIndex + 0.5) * 100) / PRESET_GRID_ROWS}%`,
                      transform: "translate(-50%, -50%)",
                      width: 22,
                      height: 22,
                      borderRadius: "999px",
                      border: selected
                        ? "2px solid #FACC15"
                        : "1px solid rgba(255,255,255,0.9)",
                      bgcolor: selected
                        ? "rgba(37,99,235,0.96)"
                        : "rgba(15,23,42,0.8)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: PRESET_GRID_MARKER_FONT_PX,
                      fontWeight: 700,
                      pointerEvents: "none",
                    }}
                  >
                    {gridId}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {hlsUrl && !panoramaSrc ? (
          <Box
            component="video"
            ref={captureVideoRef}
            muted
            playsInline
            sx={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        ) : null}

        {!showPresetPtzView && !loading && !panoramaSrc && loadError ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontSize: 12, color: "#9CA3AF", textAlign: "center" }}
            >
              {loadError}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 1,
          alignItems: "center",
        }}
      >
        {!presetGridEnabled ? (
          <Typography sx={{ fontSize: 12, color: "#374151" }}>
            Yaw <strong>{yawDeg}°</strong> · Pitch <strong>{pitchDeg}°</strong>
            {pinDirectionDeg ? ` · Pin ${pinDirectionDeg}°` : null}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "#374151" }}>
            Grid {selectedPresetGridId ?? "—"} selected
          </Typography>
        )}
        {panoramaSrc && !showPresetPtzView ? (
          <>
            {!presetGridEnabled ? (
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                onClick={centerOnHotspot}
              >
                Center on hotspot
              </Button>
            ) : null}
            <Button
              size="small"
              sx={{ textTransform: "none", ml: "auto" }}
              onClick={refreshFrame}
            >
              Refresh frame
            </Button>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
